import { createSelector } from 'reselect';

import { agentCanBind } from '../../types/Agent/Agent';
import { operationCanBind } from '../../types/Operation/Operation';
import containsString from '../../utils/containsString';
import { getAdminPageAgency } from '../adminPage/selectors';
import { getAllInsuranceAgents, getInsuranceAgents } from '../adminPanel/selectors';
import { getAgentModalSelectorProps, getSearchFromProps } from '../fromPropsSelector';
import { getOperationName, getOperationPageOperationData } from '../operationPage/selectors';

import type { Signer } from '../../scenes/lrp/containers/LrpPremiumQuoter/RequestCoverageModal/RequestCoverageModal.types';
import type InsurancePlan from '../../types/dto/InsurancePlan';
import type OperationAgentContactData from '../../types/Operation/OperationAgentContactData';
import type OperationContact from '../../types/Operation/OperationContact';
import type { RootState } from '../store';

export const getAllOperationContactOperations = (state: RootState) =>
  state.operationContact.operations;
export const getAgencyOperationsContacts = (state: RootState) =>
  state.operationContact.agencyOperationsContacts;

export const getOperationAgents = createSelector(
  getAllOperationContactOperations,
  getOperationName,
  (operationContacts, operation) => {
    if (!operationContacts[operation]) {
      return [];
    }
    return operationContacts[operation].allAgents.map(
      name => operationContacts[operation].agents[name]
    );
  }
);

export const getOperationAgentSigners = (insurancePlan: InsurancePlan) =>
  createSelector(
    getOperationAgents,
    getAllInsuranceAgents,
    getOperationPageOperationData,
    (operationAgents, allAgents, operation) => {
      if (!operation.agencyName || !operationAgents) return [];

      const agencyAgents = allAgents[operation.agencyName];
      if (!agencyAgents) return [];

      const canOperationBind = insurancePlan && operationCanBind(operation, insurancePlan);
      const agents = agencyAgents.filter(agent => {
        const found = operationAgents.some(operationAgent => operationAgent.email === agent.email);
        if (canOperationBind) {
          return found && agentCanBind(agent, insurancePlan);
        } else return found;
      });
      return agents
        .filter(
          agent =>
            !!agent.bindRequirements?.find(requirement => requirement.canBind || !canOperationBind)
        )
        .map(
          x =>
            ({
              id: x.email,
              name: `${x.firstName} ${x.lastName}`,
              title: x.title
            }) as Signer
        );
    }
  );

export const getFilteredOperationModalAgents = createSelector(
  getInsuranceAgents,
  getSearchFromProps,
  (agents, search) => {
    if (!agents?.length) {
      return [];
    }
    return agents.filter(
      ({ firstName, lastName }) =>
        containsString(firstName, search) || containsString(lastName, search)
    );
  }
);

export const getOperationModalAgents = createSelector(
  getFilteredOperationModalAgents,
  getAdminPageAgency,
  getAllOperationContactOperations,
  getAgentModalSelectorProps,
  (agents, _agency, operationContactInformation, sortProps) => {
    const { selectedOperation, activePage, perPage } = sortProps;

    const lowerCasedOperation = selectedOperation.toLowerCase();

    let operationContacts: OperationContact[] = [];
    if (operationContactInformation[lowerCasedOperation]) {
      operationContacts = Object.values(operationContactInformation[lowerCasedOperation].agents);
    }
    const mappedAgents: OperationAgentContactData[] = [];

    if (agents && operationContacts) {
      agents.forEach(agent => {
        const existingAgent = operationContacts.find(
          operationAgent => operationAgent.userId === agent.userId
        );
        if (existingAgent) {
          mappedAgents.push({ ...existingAgent, isContact: true });
        } else {
          mappedAgents.push({
            userId: agent.userId,
            firstName: agent.firstName,
            lastName: agent.lastName,
            operationName: lowerCasedOperation,
            operationFullName: '',
            title: agent.title,
            phone: agent.phoneNumber,
            email: agent.email,
            sendEndorsementNotification: false,
            primaryContact: false,
            isContact: false
          });
        }
      });
    }

    return mappedAgents.slice((activePage - 1) * perPage, perPage * activePage);
  }
);
